/**
 * Audio Base Plugin
 *
 * Library-free audio plugin providing:
 * - AudioEndpoint type for audio signal routing
 * - Pure Web Audio nodes (Oscillator, AudioOutput, Envelope)
 *
 * This plugin uses only the core's AudioContext (from RuntimeEnv).
 * ToneJs and Elementary plugins depend on this for AudioEndpoint.
 *
 * Dependencies: dataPlugin (for DataEndpoint on envelope type)
 */

// Register audio data types
declare module '@nodes/Endpoint' {
  interface DataTypeRegistry {
    audio: 'audio';
    envelope: 'envelope';
  }
}

import type { Plugin, EndpointWithContext } from '@plugins/Plugin';
import { AudioEndpoint } from './AudioEndpoint';
import type { INode } from '@nodes/INode';
import { BaseDependencies } from '@/nodes/Dependencies';
// Pure Web Audio nodes (no library dependencies)
import { oscillatorNodeDef } from './nodes/OscillatorNode/OscillatorNode';
import { audioOutputNodeDef } from './nodes/AudioOutputNode/AudioOutputNode';
import { envelopeNodeDef } from './nodes/EnvelopeNode/EnvelopeNode';
import { biquadFilterNodeDef } from './nodes/BiquadFilterNode/BiquadFilterNode';
import { delayNodeDef } from './nodes/DelayNode/DelayNode';
import type { AudioDependencies } from './nodes/AudioBaseNode';
import { DataEndpoint } from '../data/DataEndpoint';
import type { IAudioContext } from 'standardized-audio-context';

const nodes = [
  oscillatorNodeDef,
  audioOutputNodeDef,
  envelopeNodeDef,
  biquadFilterNodeDef,
  delayNodeDef
];

// AudioContext reference — set by Runtime before plugin registration
let _audioContext: IAudioContext | null = null;

/**
 * Provide the AudioContext for the audio plugin.
 * Must be called before any audio endpoints are created.
 */
export function initAudioPlugin(audioContext: AudioContext | BaseAudioContext): void {
  _audioContext = audioContext as unknown as IAudioContext;
}

/**
 * Audio base plugin.
 * Must be registered after dataPlugin (uses DataEndpoint for envelope type).
 * Call initAudioPlugin(audioContext) before registration.
 */
export const audioPlugin: Plugin = {
  id: 'audio-base',
  name: 'Audio Base',

  endpointDataTypes: ['audio', 'envelope'],
  nodes,

  createNode(type: string, id: string, deps: BaseDependencies): INode {
    const nodeDef = nodes.find(n => n.type === type);
    if (!nodeDef) {
      throw new Error(`Unknown audio node type: ${type}`);
    }
    return new nodeDef.ctor(id, { ...deps, type } as AudioDependencies);
  },

  createEndpoint(config: EndpointWithContext) {
    switch (config.endpoint.dataType) {
      case 'envelope':
        return new DataEndpoint(config, this.store!);

      case 'audio':
        if (!_audioContext) {
          throw new Error('Audio plugin not initialized — call initAudioPlugin(audioContext) first');
        }
        return new AudioEndpoint(config, _audioContext, this.store!);
    }
  },

  async registerUI({ registerViz, registerVisualiser, registerBaseMapping }) {
    const { EnvelopeVisualization } = await import('./ui/EnvelopeVisualization');
    const { AudioFrequencyViz } = await import('./ui/AudioFrequencyViz');
    const { circleVisualiser } = await import('./visualisers/CircleVisualiser');
    registerViz('Envelope', EnvelopeVisualization);
    registerViz('AudioOutput', AudioFrequencyViz);

    // Performance mode visualisers
    registerVisualiser('circle', circleVisualiser);
    registerBaseMapping('Oscillator', 'circle', { radius: 'frequency' });
  }
};
